/**
 * All Documents View Component
 * TASK-361: Document Management & Compliance System
 *
 * Platform-wide document list with filtering, search, and pagination.
 * Extracted from DocumentsPage for single responsibility.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText,
  Search,
  RefreshCw,
  Download,
  Eye,
  Filter,
  Loader2,
} from 'lucide-react';
import { documentService, ClientDocument, DocumentType, DocumentStatus } from '../../services/documentService';

/** TASK-817: how often the archive re-reads itself while an extraction is running. */
const EXTRACTION_POLL_MS = 5000;

const AllDocumentsView: React.FC = () => {
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState<DocumentType | ''>('');
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | ''>('');
  const [page, setPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const limit = 20;

  // TASK-817: `silent` skips the loading state. The full-panel "Loading documents…" spinner
  // replaces the table, which is right for a first load and wrong for a 5-second poll — the
  // rows the user is watching would vanish and come back on every tick.
  const fetchDocuments = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const response = await documentService.listDocuments({
        doc_type: docTypeFilter || undefined,
        status: statusFilter || undefined,
        limit,
        offset: page * limit,
      });
      setDocuments(response.documents);
      setTotal(response.total);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [docTypeFilter, statusFilter, page]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // TASK-817: a document accepted a moment ago is being read right now, and this table was
  // a one-shot fetch — the 'Reading…' badge would sit there until someone thought to hit
  // refresh, which is indistinguishable from an extraction that hung. Poll only while
  // something is actually in flight, and stop as soon as nothing is.
  const extractingCount = documents.filter((d) => d.status === 'processing').length;
  useEffect(() => {
    if (extractingCount === 0) return;
    const timer = setTimeout(() => fetchDocuments(true), EXTRACTION_POLL_MS);
    return () => clearTimeout(timer);
  }, [extractingCount, documents, fetchDocuments]);

  const filteredDocuments = useMemo(() =>
    documents.filter((doc) =>
      searchQuery
        ? doc.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.client_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.doc_type.toLowerCase().includes(searchQuery.toLowerCase())
        : true
    ), [documents, searchQuery]);

  const handleViewDocument = async (doc: ClientDocument) => {
    try {
      const { url } = await documentService.getPresignedUrl(doc.id);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to get document URL:', err);
    }
  };

  const handleDownloadDocument = async (doc: ClientDocument) => {
    try {
      const { url } = await documentService.getPresignedUrl(doc.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      link.click();
    } catch (err) {
      console.error('Failed to download document:', err);
    }
  };

  const docTypes: DocumentType[] = ['kyc', 'portfolio', 'risk-profile', 'aml', 'compliance', 'contracts', 'general'];
  const statuses: DocumentStatus[] = ['uploaded', 'processing', 'extracted', 'extraction_failed', 'verified', 'rejected', 'archived'];

  return (
    <div className="space-y-4">
      {/* Search and Filters Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by filename, client ID, or type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 text-sm border rounded-lg transition-colors ${
              showFilters || docTypeFilter || statusFilter
                ? 'bg-[#002B45] text-white border-[#002B45]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter size={16} />
            Filters
            {(docTypeFilter || statusFilter) && (
              <span className="bg-white text-[#002B45] text-xs px-1.5 py-0.5 rounded-full">
                {(docTypeFilter ? 1 : 0) + (statusFilter ? 1 : 0)}
              </span>
            )}
          </button>
          <button
            onClick={() => fetchDocuments()}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Filter Dropdowns */}
        {showFilters && (
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-200">
            <select
              value={docTypeFilter}
              onChange={(e) => { setDocTypeFilter(e.target.value as DocumentType | ''); setPage(0); }}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            >
              <option value="">All Types</option>
              {docTypes.map((type) => (
                <option key={type} value={type}>
                  {documentService.getDocTypeDisplayName(type)}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as DocumentStatus | ''); setPage(0); }}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            >
              <option value="">All Statuses</option>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {documentService.getStatusLabel(status)}
                </option>
              ))}
            </select>
            {(docTypeFilter || statusFilter) && (
              <button
                onClick={() => { setDocTypeFilter(''); setStatusFilter(''); setPage(0); }}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-700"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Documents Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-3 animate-spin" />
            <p className="text-sm text-gray-500">Loading documents...</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h4 className="text-lg font-medium text-gray-900 mb-1">No Documents Found</h4>
            <p className="text-sm text-gray-500">
              {searchQuery || docTypeFilter || statusFilter
                ? 'Try adjusting your search or filters.'
                : 'No documents have been uploaded yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                            {doc.filename}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {documentService.getDocTypeDisplayName(doc.doc_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {doc.client_id}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${documentService.getStatusColor(doc.status)}`}
                          title={doc.extraction_error || undefined}
                        >
                          {/* TASK-817: a document mid-extraction is doing something, and the
                              row is the only place that can say so. */}
                          {doc.status === 'processing' && (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          )}
                          {documentService.getStatusLabel(doc.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {documentService.formatFileSize(doc.file_size)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {doc.created_at
                          ? new Date(doc.created_at).toLocaleDateString('de-CH')
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleViewDocument(doc)}
                            className="p-1.5 text-gray-500 hover:text-[#002B45] hover:bg-gray-100 rounded"
                            title="View"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleDownloadDocument(doc)}
                            className="p-1.5 text-gray-500 hover:text-[#002B45] hover:bg-gray-100 rounded"
                            title="Download"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-600">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={(page + 1) * limit >= total}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AllDocumentsView;
